/**
 * The country half of a phone number: which one, and how the local part is read.
 *
 * ── WHY THIS IS A TABLE AND NOT A LIBRARY ─────────────────────────────────
 *
 * `libphonenumber-js` is 145 kB for a question this product asks in one shape:
 * pick a country, type the rest, store E.164. The table below is the ~99% of
 * BenVox's traffic, and adding a country is one line.
 *
 * ── WHY THE FLAG AND THE NAME ARE NOT IN THE TABLE ────────────────────────
 *
 * Both are derivable, and a derived value cannot go stale or arrive untranslated:
 *
 *   · the flag IS the ISO code, written in regional indicator letters — every
 *     platform renders 🇧🇷 from "BR" without an image, a sprite or a CDN;
 *   · the name comes from `Intl.DisplayNames`, which already knows "Brasil" and
 *     "Brazil" and every other locale we may add. A hand-written list would be a
 *     worse translation maintained by us — the same reasoning `shared/i18n`
 *     writes down for relative time.
 */
export interface Country {
	/** ISO 3166-1 alpha-2. Also the flag, and the key for the display name. */
	readonly iso: string;
	/** Country calling code, digits only. */
	readonly dial: string;
	/** How the local part is grouped for reading. `#` is a digit. */
	readonly mask: string;
	/** Digits in the local part, without the dial code. */
	readonly localLength: number;
}

/**
 * Brazil first because it is the default and the majority; the rest by how often
 * they actually appear.
 */
export const COUNTRIES: readonly Country[] = [
	{ iso: "BR", dial: "55", mask: "(##) #####-####", localLength: 11 },
	{ iso: "US", dial: "1", mask: "(###) ###-####", localLength: 10 },
	{ iso: "PT", dial: "351", mask: "### ### ###", localLength: 9 },
	{ iso: "AR", dial: "54", mask: "## ####-####", localLength: 10 },
	{ iso: "MX", dial: "52", mask: "## ####-####", localLength: 10 },
	{ iso: "ES", dial: "34", mask: "### ### ###", localLength: 9 },
	{ iso: "CO", dial: "57", mask: "### ### ####", localLength: 10 },
	{ iso: "CL", dial: "56", mask: "# ####-####", localLength: 9 },
	{ iso: "PY", dial: "595", mask: "### ######", localLength: 9 },
	{ iso: "UY", dial: "598", mask: "# ### ####", localLength: 8 },
	{ iso: "GB", dial: "44", mask: "#### ######", localLength: 10 },
	{ iso: "FR", dial: "33", mask: "# ## ## ## ##", localLength: 9 },
	{ iso: "DE", dial: "49", mask: "### #######", localLength: 10 },
	{ iso: "IT", dial: "39", mask: "### ### ####", localLength: 10 },
	// Greece writes its two shapes differently — "210 1234567" for an Athens line,
	// "694 123 4567" for a mobile — and this table holds ONE mask per country. The
	// 3-3-4 grouping is the mobile's own and the majority of what gets typed into a
	// contact; on a fixed number it reads as "210 123 4567", which groups the
	// Athens code correctly and only splits the subscriber part one space early.
	// The alternative, "## #### ####", is right for Athens and wrong for every
	// mobile and every other area code in the country.
	{ iso: "GR", dial: "30", mask: "### ### ####", localLength: 10 },
	{ iso: "CA", dial: "1", mask: "(###) ###-####", localLength: 10 },
];

export const DEFAULT_COUNTRY = COUNTRIES[0] as Country;

export function countryOf(iso: string): Country {
	return COUNTRIES.find((c) => c.iso === iso) ?? DEFAULT_COUNTRY;
}

/** "BR" → 🇧🇷. Regional indicator letters: A is U+1F1E6, and so on. */
export function flagOf(iso: string): string {
	return String.fromCodePoint(
		...[...iso.toUpperCase()].map((letter) => 0x1f1e6 + letter.charCodeAt(0) - 65),
	);
}

/**
 * The country's name in the reader's language.
 *
 * Falls back to the ISO code if the runtime has no `DisplayNames` — a picker
 * showing "BR" is usable; one that throws is not.
 */
export function nameOf(iso: string, locale: string): string {
	try {
		return new Intl.DisplayNames([locale], { type: "region" }).of(iso) ?? iso;
	} catch {
		return iso;
	}
}

/**
 * A stored number, split into the country and what someone typed.
 *
 * ── THE COLLISION THIS FUNCTION IS CAREFUL ABOUT ──────────────────────────
 *
 * A national Brazilian number and a foreign one are not distinguishable by
 * looking: `11987654321` is a São Paulo mobile AND a valid US number, and the
 * DDD `54` (Caxias do Sul) is Argentina's dial code. So a value SHORT enough to
 * be local for the fallback country is treated as local — never mined for a
 * dial code — and only a longer one is matched against the table, longest dial
 * code first so `+1` never swallows a `+55`.
 */
export function splitPhone(
	value: string | null | undefined,
	fallbackIso: string,
): { country: Country; local: string } {
	const fallback = countryOf(fallbackIso);
	const digits = (value ?? "").replace(/\D/g, "");

	if (!digits) return { country: fallback, local: "" };
	// A leading "+" says the dial code is there, however short the number looks.
	const international = (value ?? "").trimStart().startsWith("+");
	if (!international && digits.length <= fallback.localLength)
		return { country: fallback, local: digits };

	const byLongestDial = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);
	for (const country of byLongestDial) {
		if (!digits.startsWith(country.dial)) continue;
		const rest = digits.slice(country.dial.length);
		// ±1 digit: Brazil's ninth digit and the countries that dropped a trunk
		// prefix both make the local part vary by one.
		if (rest.length >= country.localLength - 1 && rest.length <= country.localLength + 1) {
			return { country, local: rest };
		}
	}

	if (
		digits.startsWith(fallback.dial) &&
		digits.length - fallback.dial.length <= fallback.localLength + 1
	) {
		return { country: fallback, local: digits.slice(fallback.dial.length) };
	}
	return { country: fallback, local: digits };
}

/** What gets stored: E.164 with the `+`, or empty when nothing was typed. */
export function joinPhone(country: Country, local: string): string {
	const digits = local.replace(/\D/g, "");
	return digits ? `+${country.dial}${digits}` : "";
}

/** The local part, grouped for reading. Never what is stored. */
export function maskPhone(country: Country, local: string): string {
	const digits = local.replace(/\D/g, "").slice(0, country.localLength + 1);
	let out = "";
	let at = 0;
	for (const char of country.mask) {
		if (char === "#") {
			if (at >= digits.length) break;
			out += digits[at++];
		} else if (at < digits.length) {
			out += char;
		}
	}
	// Digits past the mask (the tolerated extra one) would otherwise vanish from
	// the screen while still being stored — a number that looks wrong and is not.
	return out + digits.slice(at);
}
